// Deterministic, fact-based "Case Overview" for the dossier/brief exports.
//
// Replaces the former AI-generated prose summary (POST /cases/:id/summary).
// Mirrors the Overview tab (StructuredOverview.tsx): verified record_facts +
// record metadata, grouped into the same sections — parties, key events,
// coverage terms, financial summary with a total, and commitments/deadlines.
// No AI: every line is record metadata or a source-verified fact summary.
//
// Returns preformatted display strings so the PDF and DOCX renderers can emit
// them verbatim without re-deriving the structure.

import type { Case, CaseRecord, RecordFact } from "../types";
import { displayFactTitle, formatFactDate } from "./factMeta";
import { dedupeFacts, amountDedupKey } from "./dedupeFacts";
import { buildPartyIndex, type PartyEvidence } from "./parties";
import { formatDate } from "./formatDate";
import { buildFinancialGroups, flowOf } from "./financialFlow";
import {
  dedupeCommitments,
  commitmentState,
  COMMITMENT_STATE_LABELS,
} from "./commitments";
import { PROVENANCE_LEGEND } from "./provenance";

export interface CaseOverview {
  // "12 records · 88 verified facts · Jan 13 – Jun 4, 2026"
  headerLine: string;
  parties: string[];
  keyEvents: string[];
  coverageTerms: string[];
  // Financial summary: one chronological group per source record with
  // amount facts (see financialFlow.ts). No cross-record totals or net —
  // the only arithmetic is intra-record folds that verified exactly, which
  // render as indented "(itemized)" lines under their total.
  financialGroups: Array<{ header: string; lines: string[] }>;
  commitments: string[];
  // Plain-text provenance legend for the export renderers (the text
  // equivalent of the UI's ✓/AI marks).
  provenanceLegend: string;
  // True when no section has any content (renderers show a placeholder).
  isEmpty: boolean;
}

// Neutral deadline status — mirrors CommitmentsTable. "Date passed" makes
// no claim about whether the commitment was met. (Exports don't carry the
// user's resolved marks; resolved rows render with their date state.)
function deadlineStatus(deadline: string | null): string {
  return COMMITMENT_STATE_LABELS[
    commitmentState(deadline, false, new Date().toISOString().slice(0, 10))
  ];
}

function dateRangeLabel(records: CaseRecord[]): string | null {
  const dates = records
    .map((r) => (r.record_date ?? "").slice(0, 10))
    .filter(Boolean)
    .sort();
  if (dates.length === 0) return null;
  const first = formatDate(dates[0]!, "short") || dates[0]!;
  const last = formatDate(dates[dates.length - 1]!, "short") || dates[dates.length - 1]!;
  return first === last ? first : `${first} – ${last}`;
}

export function buildCaseOverview(
  _caseData: Case,
  records: CaseRecord[],
  facts: RecordFact[],
  evidence?: PartyEvidence,
): CaseOverview {
  const recordById = new Map(records.map((r) => [r.id, r]));
  const category = (f: RecordFact) => f.fact_category ?? "case_event";
  const caseEvent = facts.filter((f) => category(f) === "case_event");
  const coverage = facts.filter((f) => category(f) === "coverage_term");

  // Parties (cleaned + deduped, with org/role), gated on participation
  // evidence — same gate as the Overview tab. The index also canonicalizes
  // raw party values on commitment lines below (Round 10, 1D).
  const partyIndex = buildPartyIndex(facts, records, evidence);
  const parties = partyIndex.cards.map((p) =>
    [p.name, [p.org, p.role].filter(Boolean).join(" · ")]
      .filter(Boolean)
      .join(" — "),
  );

  // Key events: case_event facts that aren't amounts/commitments/deadlines
  // (those get their own sections below), deduped by quote, chronological.
  const EVENT_EXCLUDE = new Set(["amount", "commitment", "deadline"]);
  const keyEvents = dedupeFacts(
    caseEvent.filter((f) => f.date && !EVENT_EXCLUDE.has(f.fact_type)),
    recordById,
  )
    .sort((a, b) => (a.fact.date! < b.fact.date! ? -1 : 1))
    .map((d) => `${formatFactDate(d.fact.date)} — ${displayFactTitle(d.fact)}`);

  // Coverage terms.
  const coverageTerms = dedupeFacts(coverage, recordById, amountDedupKey).map(
    (d) => (d.fact.amount ? `${displayFactTitle(d.fact)} — ${d.fact.amount}` : displayFactTitle(d.fact)),
  );

  // Financial summary: per-record groups, same structure as the Overview
  // tab (FinancialSummary.tsx). ALL facts feed the fold pool — category
  // routing must not blind the arithmetic. Folded components render
  // indented.
  const financialGroups = buildFinancialGroups(facts, recordById).map((g) => ({
    header: `${g.title} — ${formatFactDate(g.date)}`,
    lines: g.lines.flatMap((l) => [
      `${displayFactTitle(l.fact)}: ${l.fact.trust_level === "low" ? "≈ " : ""}${l.fact.amount ?? "—"} [${flowOf(l.fact)}]`,
      ...l.components.map(
        (c) => `    (itemized) ${displayFactTitle(c.fact)}: ${c.fact.amount ?? "—"}`,
      ),
    ]),
  }));

  // Commitments & deadlines with a neutral status, deduped on content
  // (party + normalized text + deadline) like the Overview table.
  const commitments = dedupeCommitments(caseEvent, recordById).map((d) => {
    const f = d.fact;
    // Canonicalized party prefix (Round 10, 1D): "LIBERTY MUTUAL ALT" →
    // "Liberty Mutual Insurance". Placeholder attributions stay verbatim —
    // dropping them would un-attribute the commitment.
    const partyName = f.party
      ? partyIndex.canonicalOf(f.party) ?? f.party
      : null;
    const party = partyName ? `${partyName}: ` : "";
    const due = f.deadline ? ` (deadline ${formatFactDate(f.deadline)})` : "";
    return `${formatFactDate(f.date)} — ${party}${displayFactTitle(f)}${due} [${deadlineStatus(f.deadline)}]`;
  });

  const range = dateRangeLabel(records);
  const headerLine = [
    `${records.length} record${records.length === 1 ? "" : "s"}`,
    `${facts.length} verified fact${facts.length === 1 ? "" : "s"}`,
    range,
  ]
    .filter(Boolean)
    .join(" · ");

  const isEmpty =
    parties.length === 0 &&
    keyEvents.length === 0 &&
    coverageTerms.length === 0 &&
    financialGroups.length === 0 &&
    commitments.length === 0;

  return {
    headerLine,
    parties,
    keyEvents,
    coverageTerms,
    financialGroups,
    commitments,
    provenanceLegend: `Provenance: ${PROVENANCE_LEGEND}`,
    isEmpty,
  };
}
